import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import DOMPurify from "dompurify";

// Rend le HTML d'un email dans un iframe isolé, pour qu'un email large
// (tableaux ou blocs en largeur fixe) ne puisse jamais élargir ni casser le
// modal. Si le contenu reste plus large que le cadre malgré les contraintes
// CSS, il est réduit à l'échelle pour être visible en entier — comme le font
// les clients mail mobiles.
export default function EmailHtmlView({ html, className = "", ...rest }) {
  const [height, setHeight] = useState(240);
  const frameRef = useRef(null);
  const cleanupRef = useRef(null);

  const srcDoc = useMemo(() => {
    const clean = DOMPurify.sanitize(html || "");
    return `<!doctype html><html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<base target="_blank">
<style>
  html,body{margin:0;padding:0;background:#fff;color:#111;}
  body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
       -webkit-text-size-adjust:100%;word-break:break-word;overflow-wrap:anywhere;}
  *{box-sizing:border-box;}
  #__wrap{padding:10px;}
  img,video{max-width:100%!important;height:auto;}
  /* Les emails fixent souvent une largeur en attribut ou en style inline :
     max-width prime sur width et ramène l'élément dans le cadre. */
  table{max-width:100%!important;}
  td,th{max-width:100%;}
  a{color:#2563eb;}
</style></head><body><div id="__wrap">${clean}</div></body></html>`;
  }, [html]);

  // Mesure le contenu et, s'il déborde en largeur, le réduit à l'échelle.
  const measure = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    try {
      const doc = frame.contentDocument;
      const wrap = doc && doc.getElementById("__wrap");
      if (!wrap) return;

      const available = frame.clientWidth || frame.parentElement?.clientWidth || 0;
      if (!available) return;

      // Remise à zéro avant mesure, sinon on mesure le contenu déjà réduit
      wrap.style.transform = "none";
      wrap.style.width = "auto";

      // Largeur réelle du contenu, débordement compris. documentElement.scrollWidth
      // capte tout ce qui dépasse, quel que soit l'élément fautif.
      const contentWidth = Math.max(
        wrap.scrollWidth,
        doc.body.scrollWidth,
        doc.documentElement.scrollWidth
      );

      let scale = 1;
      if (contentWidth > available + 1) {
        // Marge de sécurité de 1px : évite qu'une imprécision sous-pixel
        // laisse le dernier pixel du contenu être coupé.
        scale = (available - 1) / contentWidth;
        wrap.style.width = `${contentWidth}px`;
        wrap.style.transformOrigin = "top left";
        wrap.style.transform = `scale(${scale})`;
      }

      const contentHeight = Math.max(
        wrap.scrollHeight,
        doc.body.scrollHeight,
        doc.documentElement.scrollHeight
      );
      const finalHeight = Math.ceil(contentHeight * scale) + 20;
      if (finalHeight > 0) setHeight(Math.min(finalHeight, 20000));
    } catch {
      /* accès au document bloqué : on garde la hauteur courante */
    }
  }, []);

  const handleLoad = useCallback(() => {
    measure();
    const frame = frameRef.current;
    if (!frame) return;

    // Nettoie les observateurs d'un précédent email
    if (cleanupRef.current) cleanupRef.current();

    const disposers = [];
    try {
      const doc = frame.contentDocument;
      const win = frame.contentWindow;

      // Re-mesure dès que le contenu change de taille : images distantes qui
      // arrivent, police web qui se charge, etc. C'est ce qui garantit que
      // l'échelle finale correspond au contenu RÉELLEMENT rendu.
      if (win && "ResizeObserver" in win && doc.body) {
        const ro = new win.ResizeObserver(() => measure());
        ro.observe(doc.body);
        disposers.push(() => ro.disconnect());
      }
      // Re-mesure après le chargement des polices web
      if (doc.fonts && doc.fonts.ready) {
        doc.fonts.ready.then(() => measure()).catch(() => {});
      }
    } catch {
      /* ignore */
    }

    // Filet de sécurité pour les navigateurs sans ResizeObserver dans l'iframe
    const timers = [150, 600, 1500, 3000].map((d) => setTimeout(measure, d));
    disposers.push(() => timers.forEach(clearTimeout));

    cleanupRef.current = () => disposers.forEach((fn) => fn());
  }, [measure]);

  // Re-mesure à la rotation / au redimensionnement de l'écran
  useEffect(() => {
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [measure]);

  return (
    // Conteneur qui borne strictement la largeur, quoi que fasse l'email.
    <div className={`w-full max-w-full overflow-hidden ${className}`}>
      <iframe
        ref={frameRef}
        title="Contenu de l'email"
        srcDoc={srcDoc}
        onLoad={handleLoad}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        scrolling="no"
        className="block bg-white rounded-md"
        // iOS Safari ignore `width` sur un iframe et l'étire à la largeur de
        // son contenu : `width:1px` + `min-width:100%` est le contournement
        // qui le force à respecter la largeur du conteneur.
        style={{ width: "1px", minWidth: "100%", maxWidth: "100%", height, border: "none" }}
        {...rest}
      />
    </div>
  );
}
